import React, { useState, useEffect, useRef } from "react";
import { Button, Typography, Card, CardContent, Container, Box, Stack} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Grid from '@mui/material/Grid2';
import MiniDrawer from "../../components/MiniDrawer.js";
import { useTheme } from '@mui/material/styles';
import AgentDetail from "./AgentDetail.js";
import { supabase } from "../../supabaseClient.js";
import { useSelector, shallowEqual } from 'react-redux';
import useAlert from '../../alert/useAlert.js';
import { useNavigate } from 'react-router-dom';

const AgentList = () => {
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [view, setView] = useState("agentList");
    const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);

    const { setAlert } = useAlert();
    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
    const hasMounted = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
      if (hasMounted.current) { return; }
      fetchSubscriptionInfo();
      hasMounted.current = true;
    }, []);

    useEffect(() => {
      loadAgents();
    }, [view]);

    const fetchSubscriptionInfo = async () => {
      try {
        if (!userDetails || !userDetails.id) return;
        
        const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();
          
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching subscription info:', error);
          return;
        }
        if(!data) {
          setSubscriptionActiveStatus(false);
          return;
        }
        if (data) {
          setSubscriptionActiveStatus(data.is_active);
        }
      } catch (error) {
        console.error('Error in fetchSubscriptionInfo:', error);
      }
    };

    const loadAgents = async () => {
      try {
        const { data, error } = await supabase.from('agents').select('*').eq('teamid', currentOrg ? currentOrg.id : '');
        if (error) {
          console.error("Error fetching agents:", error.message);
          return;
        }

        const decryptionPromises = data.map(async (agent) => {
          const [nameResult, roleResult, roleNameResult, knowledgeBaseResult, promptResult, languageResult, voiceResult, memoryResult, tasksResult] = await Promise.all([
            supabase.rpc('decrypt_text', { encrypted_data: agent.name }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.role }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.roleName }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.knowledge_base_name }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.prompt }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.language }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.voice }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.memory }),
            supabase.rpc('decrypt_text', { encrypted_data: agent.tasks }),
            agent.tasks
              ? supabase.rpc('decrypt_text', { encrypted_data: agent.tasks })
              : Promise.resolve({ data: '[]', error: null }),
          ]);
          if (nameResult.error || roleResult.error || roleNameResult.error || knowledgeBaseResult.error || promptResult.error || languageResult.error || voiceResult.error || memoryResult.error || tasksResult.error) {
            return agent;
          }
          agent.name = nameResult.data;
          agent.role = roleResult.data;
          agent.roleName = roleNameResult.data;
          agent.knowledge_base_name = knowledgeBaseResult.data;
          agent.prompt = promptResult.data;
          agent.language = languageResult.data;
          agent.voice = voiceResult.data;
          agent.memory = memoryResult.data;

          try {
            agent.tasks = tasksResult.data ? JSON.parse(tasksResult.data) : [];
          } catch (parseError) {
            console.error('Failed to parse tasks JSON:', parseError);
            agent.tasks = [];
          }
          
          return agent;
        });

        const decryptedAgents = await Promise.all(decryptionPromises);

        const sortedAgents = [...decryptedAgents].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setAgents(sortedAgents);
      } catch (error) {
        console.error("Error loading agents:", error.message);
      }
    };


    const handleAgentClick = (agent) => {
      setView("agentDetail");
      setSelectedAgent(agent);
    };
    
    const theme = useTheme();

    return (
        <Container disableGutters maxWidth={false}>
          <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
            <Grid size={{xs: 1, md: 0}}>
              <MiniDrawer />
              </Grid>
              <Grid size="grow">
                <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
                  <Grid size={12}>
                    <div style={{ padding: '24px 10vw' }}>
                      <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                        {
                          view === "agentList" && (
                            <>
                              <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                  <Typography variant="h4" sx={{marginRight: '20px'}}>AI Agent</Typography>
                                  <Button onClick={() => { setView('agentDetail'); setSelectedAgent(null); }} variant="outlined" startIcon={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                                    <Typography variant="button" sx={{color: '#FFFFFF'}}>New Agent</Typography>
                                  </Button>
                              </Grid>
                              <Grid container spacing={3} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "stretch" }}>
                                {agents.map((agent, index) => (
                                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                                    <Card 
                                      sx={{ 
                                        padding: "20px",
                                        cursor: "pointer", 
                                        borderRadius: 5, 
                                        height: '100%',
                                        minHeight: 220,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.2)'
                                      }} 
                                      onClick={() => handleAgentClick(agent)}
                                    >
                                      <CardContent 
                                        sx={{ 
                                          p: 0, 
                                          m: 0, 
                                          '&:last-child': { pb: 0 }, 
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          gap: 1.5,
                                          flex: 1,
                                          alignItems: 'flex-start',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <div style={{ width: '100%' }}>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, whiteSpace: 'normal' }}>{agent.name}</Typography>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: "#29C0BB", whiteSpace: 'normal' }}>{agent.roleName}</Typography>
                                        </div> 
                                        <div style={{ width: '100%' }}>
                                          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                                            Connected to {agent.knowledge_base_name}
                                          </Typography>
                                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'normal' }}>
                                            Last Updated on {new Date(new Date(agent.updatedAt) + (8 * 60 * 60 * 1000)).toDateString()}
                                          </Typography>
                                        </div> 
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </>
                          )
                        }
                        {
                          view === "agentDetail" && (
                            <AgentDetail 
                              onBack={() => setView("agentList")}
                              agent={selectedAgent}
                            />
                          )
                        }
                      </Grid>
                    </div>
                  </Grid>
              </Grid>
            </Grid>
          </Grid>
          {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '40px',
                  maxWidth: '500px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
                  border: '2px solid #29C0BB',
                }}
              >
                <Box
                  sx={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#E0F7FA',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0 auto 20px auto',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '36px',
                      color: '#29C0BB',
                    }}
                  >
                    ⚙️
                  </Typography>
                </Box>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#333333',
                    marginBottom: '16px',
                  }}
                >
                  Profile Setup Required
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666666',
                    marginBottom: '32px',
                    lineHeight: 1.6,
                  }}
                >
                  Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
                </Typography>
                
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#29C0BB',
                      color: '#FFFFFF',
                      borderRadius: '30px',
                      padding: '12px 24px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#259B96',
                      },
                    }}
                    onClick={() => {
                      navigate('/settings');
                    }}
                  >
                    Go to Settings
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}
          {subscriptionActiveStatus === false && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '40px',
                  maxWidth: '500px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
                  border: '2px solid #FF6B6B',
                }}
              >
                <Box
                  sx={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#FFE6E6',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0 auto 20px auto',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '36px',
                      color: '#FF6B6B',
                    }}
                  >
                    ⚠️
                  </Typography>
                </Box>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#333333',
                    marginBottom: '16px',
                  }}
                >
                  Subscription Inactive
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666666',
                    marginBottom: '32px',
                    lineHeight: 1.6,
                  }}
                >
                  Your subscription has expired and access to our platform is currently suspended. 
                  To continue using our services and manage your patients, please reactivate your subscription from the settings.
                </Typography>
                
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#29C0BB',
                      color: '#FFFFFF',
                      borderRadius: '30px',
                      padding: '12px 24px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#259B96',
                      },
                    }}
                    onClick={() => {
                      // Navigate to settings page - you might need to import useNavigate from react-router-dom
                      navigate('/settings');
                    }}
                  >
                    Subscribe Now
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}
        </Container>
    );
};

export default AgentList;